/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import {
  camelCase,
  endsWith,
  isEmpty,
  keys,
  startCase,
  template,
  toPairs
} from 'lodash';
import {
  newPasswordLinkEnabled,
  newUsernameLinkEnabled,
  rasWithdrawLinksEnabled,
  wsjEnabled
} from 'services/FeatureFlags';
import { LINK_REGEX } from 'state/IRT/IRTMessagesReducer';
import {
  SERVICE_TYPE_CORE,
  SERVICE_TYPE_HNW,
  SERVICE_TYPE_MIL
} from 'services/ServiceBenefits';
import {
  getBaseUrl,
  getENV,
  GLOBALS,
  isInternalContext,
  NODE_ENVIRONMENTS
} from "services/globals";
import {
  ALASKA_529,
  isActivePlus,
  isCollegeSavings,
  MARYLAND_529,
  NATIONAL_529,
  productMapByNumber
} from 'services/Products';
import { presentationLink, webLinkBuilder } from 'services/Links';

/**
 * Note: When running in webpack dev server mode, (window.)aam.env is managed and injected
 * by webpack. When loaded from dist (post-build), the node server will inject the aam.env
 * through index.html (check index.html class)
 */

export const SECURE_SITE = `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}`;

export const INTERNAL_SECURE_SITE = `${GLOBALS.AAM_UI_INTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}`;
export const CLIENT_PORTAL = `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/usis/client-portal/`;

export const INTERNAL_CLIENT_PORTAL = `${GLOBALS.AAM_UI_INTERNAL_BASE_URL}/usis/client-portal/`;

const advisoryWithdrawPath = rasWithdrawLinksEnabled()
  ? '/usis/transactions/advisory/withdraw'
  : '/usis/transactions/app/withdraw';

export const appBaseUrl = () => {
  let baseUrl = GLOBALS.AAM_UI_EXTERNAL_BASE_URL;
  if (window && window.location && window.location.hostname) {
    if (
      GLOBALS.AAM_UI_INTERNAL_BASE_URL.includes(window.location.hostname) &&
      !baseUrl.includes(window.location.hostname)
    ) {
      baseUrl = `https://${window.location.hostname}`;
    }
  }
  return baseUrl;
};

export const accountCenterLinks =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  GLOBALS.AAM_UI_TRP_APP_CONTEXT +
  '/external-account-center';

export const accountCenterSettings =
  GLOBALS.AAM_UI_TRP_APP_CONTEXT +
  '/external-account-center?TARGET_URL=10003659';

export const serviceLinks = {
  'Personal Services':
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/vc2/public/Retail/hSecureSelectClientServices/Personal-Services',
  'Enhanced Personal Services':
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/vc2/public/Retail/hSecureSelectClientServices/Enhanced-Personal-Services'
};

export const getStartedGoTuitionLink = {
  national529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/national-529/getting-started',
  alaska529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/alaska-529/getting-started',
  maryland529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/maryland-529/getting-started'
};

export const dashboardTuitionLink = {
  national529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/national-529/dashboard',
  alaska529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/alaska-529/dashboard',
  maryland529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/go-tuition-profile/maryland-529/dashboard'
};

export const learnMoreGoTuitionLink = {
  national529:
    'https://www.troweprice.com/personal-investing/troweprice-529/gotuition-gifting.html',
  alaska529:
    'https://www.uacollegesavings.com/how-to-get-started/a-gift-that-lasts-a-lifetime.html',
  maryland529: 'https://maryland529.com/Account-Holders/Gifting'
};

export const dashboardVideoLink = {
  national529:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/portfolioAnalytics/page/accounts-activity/analysis/custom-video.otc?productType=NAT529',
  maryland529:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/portfolioAnalytics/page/accounts-activity/analysis/custom-video.otc?productType=MD529'
};

export const rmManagerUrl = (url) => GLOBALS.AAM_UI_EXTERNAL_BASE_URL + url;

export const rmProfileUrl = template(
  GLOBALS.AAM_UI_TRP_BASE_URL +
  '/usis/personal-investing/relationship-manager/${ firstName }-${ lastName }.html'
);

export const relationshipManagerImageUrl = (imageUri) =>
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + imageUri;
export const relationshipManagerProfileUrl = (firstName, lastName) =>
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  `/personal-investing/relationship-manager/${firstName}-${lastName}.html`;

export const timeoutUrl = (products) => logoutUrl(products);
export const logoutUrl = (products) =>
  isInternalContext(getBaseUrl())
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL +
    `/usis/login/logout?sdurl=${GLOBALS.AAM_UI_INTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/post-logout?productTypes=${products}`
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    `/usis/login/logout?sdurl=${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/post-logout?productTypes=${products}`;
export const aamLogoutUrl = () => {
  return getENV() === NODE_ENVIRONMENTS.LOCAL
    ? `${window.location.origin}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/logout`
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    GLOBALS.AAM_UI_TRP_APP_CONTEXT +
    '/logout';
};

export const aamLoginUrl = (products) => {
  return `${
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/usis/login/?pdurl=/clx-tk/token-exchange/redirect/default?sdurl=${encodeURIComponent(
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    GLOBALS.AAM_UI_TRP_APP_CONTEXT +
    '?productType=' +
    products
  )}`;
};

export const deComLoginUrl = () =>
  isInternalContext(getBaseUrl())
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL +
    `/usis/login/logout?sdurl=${GLOBALS.AAM_UI_INTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/dashboard`
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    `/usis/login/logout?sdurl=${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/dashboard`;

export const messageServiceBasePath = () =>
  GLOBALS.AAM_UI_MESSAGE_SERVICE_BASE_PATH;

export const enhancedPersonalServicesBenefitsUrl = () => '/program-benefits';

export const personalServicesBenefitsUrl = () => '/program-benefits';

export const turboTaxGetStartedLink =
  'https://turbotax.intuit.com/affiliate/trowep1';

export const taxResourcesTurboTaxLink = (serviceType) => {
  switch (serviceType) {
    case SERVICE_TYPE_CORE:
      return '/program-benefits';
    case SERVICE_TYPE_HNW:
      return '/program-benefits';
    case SERVICE_TYPE_MIL:
      return '/program-benefits';
    default:
      return '/turbotax-benefits';
  }
};

export const coreServicesBenefitsUrl = () => '/program-benefits';

export const statementsFromTaxesUrl = () =>
  GLOBALS.AAM_UI_TRP_APP_CONTEXT + '/statements';

export const costbasisFromTaxesUrl = () =>
  GLOBALS.AAM_UI_TRP_APP_CONTEXT + '/profile/cost-basis';

export const phoneNumbers = {
  Default: '1-800-775-9304',
  'Personal Services': '1-800-775-9304',
  'Enhanced Personal Services': '1-888-625-9539'
};

export const accountRegistrationFormsLink =
  'https://www.troweprice.com/personal-investing/help/forms.html';


export const get529RegistrationForm = (productType) => {
  switch (productType) {
    case MARYLAND_529:
      return 'https://maryland529.com/Forms#Maryland%20College%20Investment%20Plan%20Account';
    case ALASKA_529:
      return 'https://www.alaska529plan.com/account-holder.html#account-holder-forms';
    case NATIONAL_529:
      return 'https://www.troweprice.com/personal-investing/troweprice-529/forms-and-documents.html';
    default:
      return 'https://www.troweprice.com/content/dam/iinvestor/Forms/transferowner00.pdf';
  }
};

export const highValuedClientList = [
  'Personal Services',
  'Enhanced Personal Services'
];

// TODO change EXTERNAL_BASE_URL to something like AAM_UI_AWS_BASE_URL then toggle between internal external depending on origin
export const vanityBuy = (vanityText) =>
  `${
    isInternalContext(getBaseUrl())
      ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
      : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/usis/transactions/buy?productType=MutualFund&van=${vanityText}`;

export const maryland529Calculator = 'https://tcsp.ssnc.cloud/?pid=MAR';
export const national529Calculator =
  'https://www.troweprice.com/personal-investing/troweprice-529/college-savings-calculator.html';

export const transactions = {
  mutualFund: {
    buy: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/buy?productType=MutualFund`,
    sell: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/sell?productType=MutualFund`,
    exchange: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/exchange`,
    automaticTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/automatic`,
    rmd: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/rmd-tool`
  },
  mutualFundNonRetirement: {
    add:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/transactions/buy?productType=MutualFund',
    sell:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/transactions/sell?productType=MutualFund',
    exchange: GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/transactions/exchange',
    automaticTransactions:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/transactions/automatic'
  },
  collegeSavings: {
    contribute: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/college/contribute`,
    distribute: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/college/distribute`,
    exchange: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/college/exchange`,
    automaticMonthlyContributions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/automatic`
  },
  activePlusOtherTransactions: {
    add: GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/transactions/advisory/add',
    withdraw: GLOBALS.AAM_UI_EXTERNAL_BASE_URL + advisoryWithdrawPath,
    rollOver:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/activeplus-portfolios/secure/subsequent/funding?reset=true&targetNavigation=fundingOption',
    otherTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/landing`
  },
  activePlus: {
    add: GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/transactions/advisory/add',
    withdraw: GLOBALS.AAM_UI_EXTERNAL_BASE_URL + advisoryWithdrawPath,
    exchange: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/exchange?productTypeCategory=ManagedAccount`,
    automaticTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/automatic?productTypeCategory=ManagedAccount`,
    rollOver:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/activeplus-portfolios/secure/subsequent/funding?reset=true&targetNavigation=fundingOption'
  },
  otherLinks: {
    openANewAccount:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/nasu/account-finder',
    seeAllTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/landing`,
    pamSeeAllTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/landing?returningPVLUser=true`
  },
  activeAdvisory: {
    add: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/advisory/add`,
    // Currently using placeholder link that's replaced with the "light" modal in TransactionsModal.js
    // TODO: update with real link before full RAS withdraw rollout
    withdraw: rasWithdrawLinksEnabled()
      ? GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/transactions/advisory/withdraw'
      : `${
        isInternalContext(getBaseUrl())
          ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
          : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
      }/usis/transactions/landing`,
    automaticTransactions: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/transactions/automatic`,
    transferAccounts: `${
      isInternalContext(getBaseUrl())
        ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
        : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
    }/usis/advice/checkout/transfer`
  }
};

export const fundResearchUrl = `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/tools/fund-research/account-finder`;

export const transactionsLinkBuilder = (productType, isPAM) => {
  let links = {};
  toPairs(transactions[productType]).forEach(([key, value]) => {
    if (value.indexOf('?') === -1) {
      links[key] = isPAM ? value + '?returningPVLUser=true' : value;
    } else {
      links[key] = isPAM ? value + '&returningPVLUser=true' : value;
    }
  });

  return links;
};

const filterProductKey = (productType) => {
  return isCollegeSavings(productType)
    ? 'collegeSavings'
    : keys(transactions).find((key) => key === productMapByNumber[productType]);
};

export const getAccountTransactionUrls = (
  productType,
  accountId,
  isAccountDetails,
  isRMDEligible
) => {
  const productKey = filterProductKey(productType);
  let links = isEmpty(productKey) ? {} : transactions[productKey];

  let builtLinks = [];

  for (const linkType in links) {
    const webLink = webLinkBuilder(links[linkType], accountId);
    const label = startCase(linkType);

    if (
      webLink &&
      (!isActivePlus(productType) ||
        (isActivePlus(productType) && label !== 'Exchange'))
    ) {
      builtLinks.push(presentationLink(webLink, label));
    }
  }

  return builtLinks.filter(
    ({ label }) =>
      (isAccountDetails && isRMDEligible && label !== 'Buy') ||
      (label !== 'Buy' && !isRMDEligible && label !== 'Rmd') ||
      (!isAccountDetails && label !== 'Rmd')
  );
};

export const getAccountTransactionUrlsLinkGroup = (
  productType,
  accountId,
  isAccountDetails,
  isRMDEligible
) => {
  return getAccountTransactionUrls(
    productType,
    accountId,
    isAccountDetails,
    isRMDEligible
  ).reduce((acc, curr) => ({ ...acc, [camelCase(curr.label)]: curr.link }), {});
};

export const withdrawFromSweepAccount =
  isInternalContext(getBaseUrl())
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL +
    '/usis/transactions/sell?productType=MutualFund'
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/transactions/sell?productType=MutualFund';

export const projectCashFlow =
  isInternalContext(getBaseUrl())
  ? GLOBALS.AAM_UI_INTERNAL_BASE_URL +
    '/usis/my-accounts/projected-cash-flow'
  : GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/usis/my-accounts/projected-cash-flow';

export const balanceDetailsUrl = '/usis/my-accounts/balance-details';

export const brokerageAuthTokenUrls = {
  errorUrl:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    GLOBALS.AAM_UI_TRP_APP_CONTEXT +
    '/brokerage-unavailable',
  keepAliveUrl:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL + GLOBALS.AAM_UI_TRP_APP_CONTEXT + '/ping'
};

export const ssoAuthTokenUrls = {
  errorUrl: `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/smallbusiness-unavailable`,
  ssoActivityNoteUrl:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL + GLOBALS.AAM_UI_TRP_APP_CONTEXT + '/ping',
  ssoReturnUrl:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL + `/usis/login/logout?sdurl=${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/post-logout`
};

export const educationAuthTokenUrls = {
  errorUrl: `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/help/contact/index.html`,
  contactUrl: `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/help/contact/index.html`
};

export const wallStreetJournalAuthTokenUrls = {
  // TODO : switch to use external-authorization when backend sso support is deployed to production
  authUrl: wsjEnabled()
    ? `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/external-authorization?vendor=WALL_STREET_JOURNAL`
    : 'https://www3.troweprice.com/retailtools2/vsl/selectclient/wsj/login.do?version=full&scn=Enhanced_Personal_Services_Benefits',
  errorUrl:
    GLOBALS.AAM_UI_TRP_BASE_URL + '/aaweb1/common/page/site-unavailable.otc'
};

export const factset = {
  interactiveData: 'https://www.theice.com/market-data',
  termsConditions: 'https://www.intercontinentalexchange.com/terms-of-use',
  factsetUrl:
    GLOBALS.AAM_UI_FACTSET_BASE_URL +
    'ext/fio/trowe-price-investment-services/markets/ticker-strip'
};

export const accountCenterSrcURL =
  GLOBALS.AAM_BFF_BASE_URL + '/api/accounts/external/account-center-form';

export const activePlusMyAccounts =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/usis/activeplus-portfolios/secure/my-accounts';
export const activePlusMyAccountsCWV =
  GLOBALS.AAM_APP_UI_INTERNAL_BASE_URL +
  '/usis/activeplus-portfolios/secure/my-accounts?';
const activePlusAccount =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/usis/activeplus-portfolios/secure/portfolio-details/portfolio-value';
const activePlusAccountCWV =
  GLOBALS.AAM_APP_UI_INTERNAL_BASE_URL +
  '/usis/activeplus-portfolios/secure/portfolio-details/portfolio-value';
export const activePlusAccountUrl = (
  accountId = '',
  isCustomerWebView = false,
  uberId,
  hasAgreementId = false
) => {
  if (hasAgreementId) {
    return !isCustomerWebView || !uberId
      ? `${activePlusAccount}`
      : `${activePlusAccountCWV}?uid=${uberId}`;
  }
  return !isCustomerWebView || !uberId
    ? `${activePlusAccount}?agreementID=${accountId}`
    : `${activePlusAccountCWV}?agreementID=${accountId}&uid=${uberId}`;
};

export const activePlusMyAccountsUrl = (isCWV = false, uberId) => {
  return !isCWV || !uberId
    ? activePlusMyAccounts
    : `${activePlusMyAccountsCWV}uid=${uberId}`;
};

export const navigationUrls = {
  futurePath:
    GLOBALS.AAM_UI_TRP_BASE_URL + '/prp/futurepath/page/auth/welcome.otc',
  assetAllocation:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/portfolioAnalytics/page/accounts-activity/analysis/asset-allocation-analysis.otc',
  accountCenter:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/portfolioAnalytics/page/accounts-activity/external-account-center.otc'
};

export const fundBookUrlByTicker = (ticker) =>
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/personal-investing/tools/fund-research/' +
  ticker;

export const header529Urls = {
  hasMaryland529Only: {
    openAnAccount:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/nasu/start/college-savings/maryland'
  },
  hasAlaska529Only: {
    openAnAccount:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/nasu/start/college-savings/alaska'
  },
  hasNational529Only: {
    openAnAccount:
      GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
      '/usis/nasu/start/college-savings/national'
  }
};

const openAnAccountLink = (accountType) =>
  header529Urls[accountType]
    ? header529Urls[accountType]['openAnAccount']
    : transactions.otherLinks.openANewAccount;

export const openAnAccountUrl = (view) => {
  switch (true) {
    case view.hasMaryland529Only:
      return openAnAccountLink('hasMaryland529Only');
    case view.hasNational529Only:
      return openAnAccountLink('hasNational529Only');
    case view.hasAlaska529Only:
      return openAnAccountLink('hasAlaska529Only');
    default:
      return openAnAccountLink(view);
  }
};

export const directDeposit529Url = (view) => {
  switch (true) {
    case view.hasMaryland529 && !view.hasNational529 && !view.hasAlaska529:
      return 'https://maryland529.com/Portals/0/Forms/Tax_Refund_Direct_Deposit_Form.pdf';
    case view.hasNational529 && !view.hasMaryland529 && !view.hasAlaska529:
      return 'https://www.troweprice.com/content/dam/iinvestor/Forms/Direct_Deposit_Form.pdf';
    case view.hasAlaska529 && !view.hasMaryland529 && !view.hasNational529:
      return 'https://www.alaska529plan.com/content/dam/alaska529/forms/Direct_Deposit_Form.pdf';
  }
};

export const menu529 = {
  hasNational529Only: {
    myAccounts: {
      label: 'My Accounts',
      url: SECURE_SITE,
      subMenu: {
        dashboard: {
          label: 'Dashboard',
          url: SECURE_SITE
        },
        accounts: {
          label: 'Accounts',
          url: SECURE_SITE + '/accounts'
        },
        transactions: {
          label: 'Transactions',
          url: SECURE_SITE + '/transactions'
        },
        performance: {
          label: 'Performance',
          url: SECURE_SITE + '/performance'
        },
        taxes: {
          label: 'Taxes',
          url: SECURE_SITE + '/taxes'
        },
        statements: {
          label: 'Statements',
          url: SECURE_SITE + '/statements'
        },
        profile: {
          label: 'Profile',
          url: SECURE_SITE + '/profile'
        },
        startATransaction: {
          label: 'Start a Transaction',
          url:
            GLOBALS.AAM_UI_TRP_BASE_URL +
            '/aaweb1/transactions/page/transact-trade.otc'
        }
      }
    },
    more: {
      label: 'More',
      subMenu: {
        dailyClosingPrices: {
          label: 'Daily Closing Prices',
          url:
            SECURE_SITE + '/personal-investing/tools/fund-research/daily-prices'
        },
        mutualFundsOverview: {
          label: 'Mutual Funds Overview',
          url: SECURE_SITE + '/personal-investing/mutual-funds/index.html'
        },
        historicalPerformance: {
          label: 'Historical Performance',
          url:
            SECURE_SITE +
            '/personal-investing/tools/fund-research/historical-performance'
        },
        siteMap: {
          label: 'Site Map',
          url: SECURE_SITE + '/personal-investing/sitemap.html?scn=footer'
        }
      }
    }
  },
  hasAlaska529Only: {
    myAccounts: {
      label: 'My Accounts',
      url: SECURE_SITE,
      subMenu: {
        dashboard: {
          label: 'Dashboard',
          url: SECURE_SITE
        },
        accounts: {
          label: 'Accounts',
          url: SECURE_SITE + '/accounts'
        },
        transactions: {
          label: 'Transactions',
          url: SECURE_SITE + '/transactions'
        },
        performance: {
          label: 'Performance',
          url: SECURE_SITE + '/performance'
        },
        taxes: {
          label: 'Taxes',
          url: SECURE_SITE + '/taxes'
        },
        statements: {
          label: 'Statements',
          url: SECURE_SITE + '/statements'
        },
        profile: {
          label: 'Profile',
          url: SECURE_SITE + '/profile'
        },
        startATransaction: {
          label: 'Start a Transaction',
          url:
            GLOBALS.AAM_UI_TRP_BASE_URL +
            '/aaweb1/transactions/page/transact-trade.otc'
        }
      }
    },
    more: {
      label: 'More',
      subMenu: {
        ourPlans: {
          label: 'Why Alaska 529',
          url: 'https://www.alaska529plan.com/why-alaska-529.html'
        },
        toolsAndResources: {
          label: 'Account Holder Tools',
          url: 'https://www.alaska529plan.com/account-holder.html'
        },
        investmentOptions: {
          label: 'Investment Options',
          url: 'https://www.alaska529plan.com/investment-options.html'
        }
      }
    }
  },
  hasMaryland529Only: {
    myAccounts: {
      label: 'My Accounts',
      url: SECURE_SITE,
      subMenu: {
        dashboard: {
          label: 'Dashboard',
          url: SECURE_SITE
        },
        accounts: {
          label: 'Accounts',
          url: SECURE_SITE + '/accounts'
        },
        transactions: {
          label: 'Transactions',
          url: SECURE_SITE + '/transactions'
        },
        performance: {
          label: 'Performance',
          url: SECURE_SITE + '/performance'
        },
        taxes: {
          label: 'Taxes',
          url: SECURE_SITE + '/taxes'
        },
        statements: {
          label: 'Statements',
          url: SECURE_SITE + '/statements'
        },
        profile: {
          label: 'Profile',
          url: SECURE_SITE + '/profile'
        },
        startATransaction: {
          label: 'Start a Transaction',
          url:
            GLOBALS.AAM_UI_TRP_BASE_URL +
            '/aaweb1/transactions/page/transact-trade.otc'
        }
      }
    },
    more: {
      label: 'More',
      subMenu: {
        ourPlans: {
          label: 'Our Plans',
          url: 'https://maryland529.com/WhyMD529'
        },
        toolsAndResources: {
          label: 'Tools & Resources',
          url: 'https://maryland529.com/Account-Holders/Maryland-College-Investment-Plan'
        },
        investmentOptions: {
          label: 'Investment Options',
          url: 'https://maryland529.com/college-savings-plans-of-maryland/maryland-college-investment-plan/investment-option'
        }
      }
    }
  }
};

export const privateAssetManagementUrl =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/personal-investing/tools/pamwebsite';

export const workplaceRetirementAccountsUrl =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/workplace/en/participant/home.html';

export const clientPortalDashboardUrl =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/client-portal/dashboard';

export const clientPortalDashboardInternalUrl =
  GLOBALS.AAM_UI_INTERNAL_BASE_URL + '/usis/client-portal/dashboard';

export const retailTracAccountsUrl =
  'https://betaretirement.financialtrans.com/trpsmbret';

export const requiredMinimumDistributionUrl = () =>
  appBaseUrl() + '/usis/rmd-tool';

export const collegeSavingsAccountLink = {
  alaska529: 'https://www.alaska529plan.com/investment-options.html',
  maryland529: 'https://maryland529.com/Investment-Plan/Investment-Options',
  national529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/troweprice-529/investment-options.html'
};

export const collegeSavingsPerformanceLink = {
  alaska529: 'https://www.alaska529plan.com/plan-performance.html',
  maryland529: 'https://maryland529.com/Investment-Plan/Investment-Performance',
  national529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/troweprice-529/historical-performance.html'
};

export const collegeSavingsDailyPriceLink = {
  alaska529: 'https://www.alaska529plan.com/daily-portfolio-prices.html',
  maryland529: 'https://maryland529.com/Investment-Plan/Investment-Performance',
  national529:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/troweprice-529/daily-prices.html'
};

export const footerLinks = {
  wallStreetJournalUrl: 'https://wsj.com',
  companyOverviewUrl: 'https://www.troweprice.com/corporate/en/what-we-do.html',
  socialResponsibilityUrl:
    'https://www.troweprice.com/corporate/en/what-sets-us-apart/corporate-responsibility.html',
  careersUrl: 'https://www.troweprice.com/corporate/en/careers.html',
  investorRelationsUrl: 'https://troweprice.gcs-web.com/',
  pressReleasesUrl: 'https://www.troweprice.com/corporate/en/press.html',
  privacyPolicyUrl:
    'https://www.troweprice.com/personal-investing/help/policies-and-security/privacy.html',
  alaska529: {
    privacyPolicyUrl: 'https://www.alaska529plan.com/privacy-policy.html',
    contactUsUrl: 'https://www.alaska529plan.com/index.html/#contact-us'
  },

  maryland529: {
    privacyPolicyUrl:
      'https://maryland529.com/About-Us/Policies-and-Statements',
    contactUsUrl: 'https://maryland529.com/About-Us/Contact-Us'
  },
  national529: {
    privacyPolicyUrl:
      'https://www.troweprice.com/personal-investing/troweprice-529/privacy-policy.html',
    contactUsUrl:
      'https://www.troweprice.com/personal-investing/troweprice-529/contact-us.html'
  },
  siteMapUrl: 'https://www.troweprice.com/personal-investing/sitemap.html',
  termsOfUseUrl:
    'https://www.troweprice.com/personal-investing/help/terms-of-use.html',
  securityMeasuresUrl:
    'https://www.troweprice.com/personal-investing/help/policies-and-security/security.html',
  legalInformationUrl:
    'https://www.troweprice.com/personal-investing/help/policies-and-security/legal-information.html',
  customerAgreementUrl:
    'https://www.troweprice.com/personal-investing/help/policies-and-security/customer-agreement.html',
  mobileSolutionsUrl:
    'https://www.troweprice.com/corporate/en/mobile-solutions.html',
  contactUsUrl:
    'https://www.troweprice.com/personal-investing/help/contact/index.html',
  alaska529Change:
    'https://www.alaska529plan.com/content/dam/alaska529/forms/Beneficiary_Portfolio_Change_Form.pdf',
  maryland529Change:
    'https://maryland529.com/Portals/0/Forms/Beneficiary_Portfolio_Change_Form.pdf',
  national529Change:
    'https://www.troweprice.com/content/dam/iinvestor/Forms/trpChangeYourBeneficiaryForm.pdf',
  prospectusUrl:
    'https://www.troweprice.com/personal-investing/mutual-funds/prospectuses-reports.html',
  copyrightUrl:
    'https://troweprice.netxinvestor.com/web/troweprice/copyright-terms-of-use/copyright-terms-of-usechild',
  md529DecomUrl:
    'https://cdn.unite529.com/jcdn/files/MDD/pdfs/Investment_Plan_Account_Holder_Transition_Guide.pdf',
  iraBeneForm:
    'https://www.troweprice.com/content/dam/iinvestor/Forms/IRABeneCh.pdf',
  nat529DecomUrl:
    'https://cdn.unite529.com/jcdn/files/PDF/pdfs/TRPCSP/T_Rowe_Price_College_Savings_Plan_Account_Holder_Transition_Guide.pdf',
  ak529DecomUrl:
    'https://cdn.unite529.com/jcdn/files/AKD/pdfs/Alaska_529_Account_Owner_Transition_Guide.pdf'
};

export const footerText = {
  branding529Text:
    'To change the beneficiary (future student) on your college savings account(s), please use the',
  multi529ChangeFormText:
    'To change the beneficiary (future student) on your college savings account(s), please',
  inheritedRothOrTraditionalIRAText: 'Inherited IRA: Some states may restrict adding beneficiaries on Inherited IRAs. Consult an attorney or the appropriate state authority.',
  brkgeROTHText:
    'To add/update your Brokerage Advantage account beneficiaries, please'
};

export const maryland529MatchLinks = {
  applyNow: isInternalContext(getBaseUrl())
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL + '/usis/md529-state-contribution/'
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/md529-state-contribution/',
  learnMore: 'https://maryland529.com/Save4College'
};

export const externalInvestmentLinks = {
  learnMore: '',
  linkAnAccount:
    GLOBALS.AAM_BFF_BASE_URL +
    '/api/accounts/external/link-account-wizard-form',
  getBalance: '',
  logout:
    GLOBALS.AAM_UI_TRP_AWS_ENVIRONMENT === 'prod'
      ? 'https://finapp.trowepricepfm3.yodlee.com/authenticate/trowepricepfm3'
      : 'https://finapp.trowepricepfm3stage.yodlee.com/authenticate/trowepricepfm3/logout',
  linkAnAccountExternal:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/portfolioAnalytics/page/common/progress-bar.otc?url1=/portfolioAnalytics/page/accounts-activity/external-accounts/launch-external-application.otc%3Fa=b&command=launchLinkAccountWizard'
};

export const futurePathLink =
  GLOBALS.AAM_UI_TRP_BASE_URL +
  '/prp/futurepath/page/auth/welcome.otc?referrer=accountAccess';

export const nasuAccountsLink = `${
  isInternalContext(getBaseUrl())
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
}/usis/nasu/profile/email`;

export const nasuAccountLinkV2 = (applicationID) => {
     return `${
    isInternalContext(getBaseUrl())
      ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
      : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/personal-investing/new-account/setup/return-journey/welcome?applicationID=${applicationID}`;
};

export const moreNavigationLinks = {
  default: {
    mfOverview: {
      label: 'Mutual Funds Overview',
      url:
        GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
        '/personal-investing/mutual-funds/index.html'
    },
    dailyClosingPrices: {
      label: 'Daily Closing Prices',
      url:
        GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
        '/personal-investing/tools/fund-research/daily-prices'
    },
    historicalPerformance: {
      label: 'Historical Performance',
      url:
        GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
        '/personal-investing/tools/fund-research/historical-performance'
    },
    siteMap: {
      label: 'Site Map',
      url:
        GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
        '/personal-investing/sitemap.html?scn=footer'
    }
  },
  hasMaryland529Only: {
    ourPlans: {
      label: 'Our Plans',
      url: 'https://maryland529.com/WhyMD529'
    },
    toolsAndResources: {
      label: 'Tools & Resources',
      url: 'https://maryland529.com/Account-Holders/Maryland-College-Investment-Plan'
    },
    investmentOptions: {
      label: 'Investment Options',
      url: 'https://maryland529.com/Investment-Plan/Investment-Options'
    }
  },
  hasAlaska529Only: {
    ourPlan: {
      label: 'Why Alaska 529',
      url: 'https://www.alaska529plan.com/why-alaska-529.html'
    },
    toolsAndResources: {
      label: 'Account Holder Tools',
      url: 'https://www.alaska529plan.com/account-holder.html'
    },
    investmentOptions: {
      label: 'Investment Options',
      url: 'https://www.alaska529plan.com/investment-options.html?v_linkcomp=aalink&v_link=Investment_Options&v_linkplmt=TN'
    }
  }
};

export const transactionFeeLinks = {
  subscribeToElectronicDelivery:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/myProfile/page/profile-preferences/email-preferences/edit.otc',
  selectClientServices:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/advice-and-planning/client-benefits.html',
  accountServiceFee:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/help/fees-and-minimums.html',
  smallBusinessRetirementPlans:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/retirement/small-business-retirement-plans.html',
  brokerageAndBrokerageAdvantage:
    'https://www.troweprice.com/personal-investing/products-and-services/brokerage.html',
  redemptionFeePolicy:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/help/fees-and-minimums.html'
};

export const taxInformationLinks = {
  taxFormMailingSchedule:
    'https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/prepare-your-taxes/tax-information-mailing-schedule.html',
  commonTaxAndIRAQuestions:
    'https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/learn-more-about-taxes/tax-and-ira-frequently-asked-questions.html'
};

export const taxPlanningResourceLinks = {
  dividendDistributions:
    'https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/dividend-distributions.html',
  taxPlanningResources:
    'https://www.troweprice.com/personal-investing/advice-and-planning/tax-planning.html'
};

export const taxFormsNationalOnlyUrl =
  'https://www.troweprice.com/personal-investing/troweprice-529/forms-and-documents.html';

export const usernameUrl = (productClass) => {
  return newUsernameLinkEnabled()
    ? GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/login/update/username?pdurl=/clx-tk/token-exchange/redirect/default%3fsdurl=' +
    SECURE_SITE +
    '/profile?productType=' +
    productClass
    : GLOBALS.AAM_UI_TRP_BASE_URL +
    '/clx/login/update.otc?updateType=user_name&pdurl=/aaweb1/myProfile/page/profile-preferences.otc';
};

export const passwordUrl = (productClass) => {
  return newPasswordLinkEnabled()
    ? GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/login/update/password?pdurl=/clx-tk/token-exchange/redirect/default%3fsdurl=' +
    SECURE_SITE +
    '/profile?productType=' +
    productClass
    : GLOBALS.AAM_UI_TRP_BASE_URL +
    '/clx/login/update.otc?updateType=password&pdurl=/aaweb1/myProfile/page/profile-preferences.otc';
};

export const preferencesLinks = {
  automaticTransactionsLink: `${
    isInternalContext(getBaseUrl())
      ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
      : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/usis/transactions/automatic`,
  checkWritingLink:
    'https://individual.troweprice.com/staticFiles/Retail/Shared/PDFs/MFCheckwriting.pdf',
  nameChangeForm:
    'https://individual.troweprice.com/staticFiles/Retail/Shared/PDFs/namechange.pdf',
  securityQuestions:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/login/update/kba?pdurl=/clx-tk/token-exchange/redirect/default%3fsdurl=' +
    SECURE_SITE +
    '/profile',
  multiFactorAuthentication:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/login/update?pdurl=/clx-tk/token-exchange/redirect/default%3fsdurl=' +
    SECURE_SITE +
    '/profile',
  managePassKey:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/usis/login/update/passkeys?pdurl=/clx-tk/token-exchange/redirect/default%3fsdurl=' +
    SECURE_SITE +
    '/profile',
  addUpdateViewAccess:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/myProfile/page/profile-preferences/grant-view-access.otc?reset=true',
  updateViewAccess:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/myProfile/page/profile-preferences/edit-view-access/edit.otc?reset=true',
  accountProtectionProgram:
    'https://www.troweprice.com/personal-investing/help/policies-and-security/account-protection-program.html',
  costBasisRegulation:
    'https://www.troweprice.com/personal-investing/planning-and-research/tax-planning/learn-more-about-taxes/cost-basis-accounting-and-calculation.html',
  workplaceRetirementProfileUrl:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/rps/rws/account/participant/profile.otc',
  BeneficiaryWorkplaceRetirementProfileUrl:'https://www.troweprice.com/workplace/en/participant/profile/index.html'
};

export const preferencesLink = SECURE_SITE + '/profile';

export const productLinkBuilder = (link, productClass) => {
  return `${link}?productType=${productClass}`;
};

export const personalizedAdviceLink =
  'https://www.troweprice.com/personal-investing/advice/index.html';

export const financialPlanningServiceLink =
  'https://www.troweprice.com/usis/advice/fps';

export const enhancedPersonalServicesLinks = {
  selectClientServices:
    'https://www.troweprice.com/personal-investing/advice-and-planning/client-benefits.html#Services',
  firstNoticeLink: SECURE_SITE + '/profile/subscriptions',
  welcomeKit:
    'https://www.troweprice.com/content/dam/iinvestor/select-client-services/enhanced-personal-services/enhanced-personal-services-welcome-kit.pdf',
  morningstarBenefits:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/retailtools/mstar/pages/mfi/selectclient/psMStarBegin.jsp',
  turboTaxAffiliate: 'https://turbotax.intuit.com/affiliate/6jqpfte5',
  charitableGiving:
    GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
    '/personal-investing/planning-and-research/program-for-charitable-giving.html',
  personalizedInvestingAdvice: personalizedAdviceLink
};

export const coreServicesLinks = {
  directDeposit:
    GLOBALS.AAM_UI_TRP_BASE_URL +
    '/aaweb1/transactions/page/transact-trade/direct-deposit-tax-refund.otc?',
  collegeSavings:
    'https://www.troweprice.com/personal-investing/products-and-services/college-savings-plans.html',
  turboTaxLink:
    'https://turbotax.intuit.com/microsite/home.htm?priorityCode=3468339796&cid=all_trowep1_aff_3468339796'
};

export const personalServicesLinks = {
  turboTaxLink: 'https://turbotax.intuit.com/affiliate/g5fyo7u3',
  firstNoticeLink: SECURE_SITE + '/profile/subscriptions',
  charitableGivingLink:
    'https://www.troweprice.com/personal-investing/planning-and-research/program-for-charitable-giving.html',
  brokerageTradesLink:
    'https://www.troweprice.com/personal-investing/products-and-services/brokerage.html',
  iraLink: `${
    isInternalContext(getBaseUrl())
      ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
      : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/usis/transactions/landing`,
  morningstarLink:
    'https://www3.troweprice.com/retailtools/mstar/pages/mfi/selectclient/psMStarBegin.jsp',
  personalizedAdviceLink: personalizedAdviceLink,
  welcomeKit:
    'https://www.troweprice.com/content/dam/iinvestor/select-client-services/personal-services/personal-services-welcome-kit.pdf'
};

export const turboTaxDiscountLinks = {
  getStartedLink:
    'https://turbotax.intuit.com/microsite/home.htm?priorityCode=3468339796&cid=all_trowep1_aff_3468339796',
  turboGuaranteeLink: 'https://turbotax.intuit.com/lp/yoy/guarantees.jsp'
};

export const NET_X_LOGOUT_URL = GLOBALS.AAM_UI_NET_X_LOGOUT_URL;
export const NET_X_MESSAGE_URL = GLOBALS.AAM_UI_NET_X_MESSAGE_URL;

export const contributionLimitsUrl =
  'https://www.troweprice.com/personal-investing/retirement/ira/roth-or-traditional-ira.html';

export const withTrailingSlash = (value) => {
  if (!value) {
    return '/';
  }
  return !endsWith(value, '/') ? value + '/' : value;
};

export const withoutTrailingSlash = (value) => {
  return value && endsWith(value, '/')
    ? value.substring(0, value.length - 1)
    : value;
};

export const getPathnameEnd = (value) => {
  const pathNameSplit = value
    ? value.split(GLOBALS.AAM_UI_TRP_APP_CONTEXT)
    : '';
  if (pathNameSplit === '' || pathNameSplit[pathNameSplit.length - 1] === '') {
    return '';
  }
  const rawPathName = pathNameSplit[pathNameSplit.length - 1];
  const cleanPathName = rawPathName ? rawPathName.split('/error') : '/error';
  const pathName = cleanPathName[cleanPathName.length - 1];
  return withoutTrailingSlash(pathName);
};

export const savedAppUrl =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/nasu/profile/email';

export const linkBuilder = (link, view) => {
  const { isPAM } = view || {};

  if (link && link.indexOf('.otc') !== -1 && isPAM) {
    return `${link}${
      link.indexOf('?') === -1
        ? '?returningPVLUser=true'
        : '&returningPVLUser=true'
    }`;
  }
  return link;
};

export const feesAndMinimumsUrl =
  'https://www.troweprice.com/personal-investing/help/fees-and-minimums.html';

export const yodleeAggregationServiceTerms =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/content/dam/iinvestor/products-and-services/adv/Aggregation-Services-Terms.pdf';

export const brkInterceptGuide = 
'https://www.troweprice.com/personal-investing/campaign/brokerage-guide.html';

export const promoLinkBuilder = (messageText, messageId) => {
  if (messageText && messageId) {
    if (messageText.match(LINK_REGEX).length) {
      const result = LINK_REGEX.exec(messageText);
      const [, linkElementContent] = result;
      if (linkElementContent) {
        const messageIdExtension =
          linkElementContent.indexOf('?') !== -1
            ? `&messID=${messageId}`
            : `?messID=${messageId}`;
        return linkElementContent + messageIdExtension;
      }
    }
  }
  return null;
};

export const linkedBankAccountsLinks = {
  addNewBank: `${
    isInternalContext(getBaseUrl())
      ? GLOBALS.AAM_UI_INTERNAL_BASE_URL
      : GLOBALS.AAM_UI_EXTERNAL_BASE_URL
  }/usis/transactions/addbank`,
  mutualFundForm:
    'https://individual.troweprice.com/staticFiles/Retail/Shared/PDFs/shrhldrsrvcs.pdf',
  national529Form:
    'https://www.troweprice.com/content/dam/iinvestor/Forms/trpUpdateYourAccServicesForm.pdf',
  maryland529Form:
    'https://maryland529.com/Portals/0/Forms/Nov 2020 MCIP Updated Forms/MCIP Acct Services Form_F5M3MSRV.wbi_RE.pdf?ver=bZhv4jkjib4EW8bNT5VPDA%3d%3d',
  alaska529Form:
    'https://www.alaska529plan.com/content/dam/alaska529/forms/Account_Services_Form.pdf'
};

export const updatedLinkedBankAccountsLinks = {
  mutualFundForm:
    'https://www.troweprice.com/content/dam/iinvestor/Forms/shrhldrsrvcs.pdf',
  national529Form:
    'https://www.troweprice.com/content/dam/iinvestor/Forms/trpUpdateYourAccServicesForm.pdf',
  maryland529Form:
    'https://maryland529.com/Portals/0/Forms/Account_Services_Form.pdf',
  alaska529Form:
    'https://www.alaska529plan.com/content/dam/alaska529/forms/Account_Services_Form.pdf'
};

export const guestUserHomepageUrl = `${GLOBALS.AAM_INTERNAL_BASE_URL}/personal-investing/guest-user.html`;

export const morningstarHomepageUrl = 'https://www.morningstar.com/';

export const marginalRateTableUrl = 'https://www.irs.gov/pub/irs-pdf/fw4r.pdf';

export const lifeEventsManagerLink =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/personal-investing/resources/planning/life-events/index.html';

export const kiplingerExistingSubscriptionLink =
  'https://service.kiplinger.com/login?redirect=%2F';

export const benefitsInsightsLink =
  'https://www.troweprice.com/personal-investing/resources/insights/index.html';

export const transferExternalAsset =
  'https://www.troweprice.com/personal-investing/account-finder/transfers.html';

export const leverageHouseholdAssets =
  'https://www.troweprice.com/personal-investing/about/client-benefits/faqs.html#summit-householding';

export const getWorkplaceRetirementUrl = (
  isCustomerWebView,
  delegatedUserId
) => {
  if (isCustomerWebView && delegatedUserId) {
    return (
      GLOBALS.AAM_INTERNAL_BASE_URL +
      `/rps/rws/account/participant/webview.otc?userid=${delegatedUserId}`
    );
  }
  return workplaceRetirementAccountsUrl;
};

export const smpCollegeSavingsProgramInfoLink =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  '/personal-investing/about/client-benefits/index.html';

export const planningDetailsUrl = (isCustomerWebView) =>
  isCustomerWebView
    ? GLOBALS.AAM_UI_INTERNAL_BASE_URL + '/usis/advice/ras-home/'
    : GLOBALS.AAM_UI_EXTERNAL_BASE_URL + '/usis/advice/ras-home/';

export const turbotaxIntuitUrl =
  'https://turbotax.intuit.com/microsite/home.htm?priorityCode=3468339796&cid=all_trowep1_aff_3468339796';

export const AEM = {
  FRAGMENTS: {
    summit529Disclosure: `${getBaseUrl()}/personal-investing/about/client-benefits/summit-disclosures/529.model.json`,
    summitIClass: `${getBaseUrl()}/personal-investing/about/client-benefits/summit-disclosures/i-class.model.json`,
    paperlessDisclosure: `${getBaseUrl()}/personal-investing/terms-and-conditions/paperless.model.json`,
    yodleeConsentDisclosure: `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/graphql/execute.json/iinvestor/getTeaserCfModelByPath%3Bpath=/content/dam/iinvestor/disclosures/yodlee-consent-modal-fyi`,
    brokerageInterceptMessage: `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/graphql/execute.json/iinvestor/getTeasersByVariation%3Bpath=/content/dam/iinvestor/teaser-content/brokerage-intercept-message`
  }
};

export const brokerageCommissionsAndFees =
  'https://www.troweprice.com/personal-investing/accounts/brokerage/commissions-and-fees.html';

export const loginUrl = `${
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL
}/usis/login/?pdurl=/clx-tk/token-exchange/redirect/default?sdurl=${encodeURIComponent(
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL +
  GLOBALS.AAM_UI_TRP_APP_CONTEXT +
  '/dashboard'
)}`;

export const aamSdurl =
  GLOBALS.AAM_UI_EXTERNAL_BASE_URL + GLOBALS.AAM_UI_TRP_APP_CONTEXT + '/';
export const aamPdurl = '/clx-tk/token-exchange/redirect/default%3f';

export const LOGIN = `${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/usis/login?pdurl=${aamPdurl}sdurl=${aamSdurl}`;
